// Card #1797 — the Blank mode type's logo source is a COMMITTED BRAND
// ASSET, not `Show.logoKey` (#1058)'s S3 upload path and not view/show/org
// config (design decision on the card, superseding this repo's earlier
// working assumption in packages/brand/venues/README.md that #1797 would
// wire up to logoKey — it does not). A mode carries a bare string key
// (`mode.logoAsset`, e.g. "wec-vertical-white") and THIS map is the only
// place that key is ever resolved to an actual image. Deliberately a
// hardcoded lookup, never string-concatenation into a path
// (`brand/venues/${key}.png`) — that would let a bad/typo'd key produce a
// broken <img> src. An unset, unrecognized, or otherwise invalid key always
// falls back to the Fox Village default via resolveBlankLogoAsset's `||`
// below, so the board is never left with nothing to show (#1798: "NEVER
// show an error message on the scoreboard" — an empty board reads exactly
// like a dead projector).
//
// 🔴 The default is the Fox Village FOX MARK, not the favicon. This card
// originally used packages/brand/favicon/favicon.svg on the reasoning that
// it was the only SQUARE Fox Village artwork (FoxVillageLogo.jsx being a
// 219x332 portrait mark, report-defs' LOGO_WIDE_SVG a 1201x333 lockup).
// That reasoning was wrong twice over. Jaybill, 2026-08-20: the favicon is
// "vestigial and NOT correct" — blown up to this view's centrepiece scale it
// reads as a box/crate, not a brand mark, which is exactly what #1798's
// universal fallback must not look like. And "square" was never a real
// constraint: a portrait mark centred in a centred flex box is fine.
//
// packages/brand/logo/fox-village-mark-white.svg is the SAME artwork as
// brand's FoxVillageLogo.jsx (identical viewBox 0 0 219.26 332), committed
// as a standalone file with its fill hardcoded white rather than imported as
// that JSX component, because resolveBlankLogoAsset's contract is a plain
// <img> src STRING (pinned by blankLogoAssets.test.js) and the venue assets
// are rasters that could never be components anyway. White is correct
// unconditionally: the board is always dark (Jaybill, 2026-08-20), which is
// also why this file no longer reasons about theme background at all.
import foxVillageMark from "brand/logo/fox-village-mark-white.svg";
import wecVerticalWhite from "brand/venues/wec-vertical-white.png";

// The key a mode carries when it wants the default explicitly, and the key
// resolveBlankLogoAsset falls back to for anything else unrecognized.
export const DEFAULT_BLANK_LOGO_ASSET_KEY = "fox-village-mark";

// Card #1797 AC3 — "a small explicit allow-list map in score-fe". Adding a
// venue asset means adding BOTH a row here (image -> key) AND a row in
// BLANK_LOGO_ASSET_OPTIONS below (key -> human label for the remote's Logo
// picker) — the two are kept as separate exports so the remote control's
// dropdown never needs to import the actual image binaries.
const BLANK_LOGO_ASSETS = {
  [DEFAULT_BLANK_LOGO_ASSET_KEY]: foxVillageMark,
  "wec-vertical-white": wecVerticalWhite,
};

// Human-facing labels for the remote's mode editor Logo picker
// (ModeEditorDialog.jsx), in the same key-order as BLANK_LOGO_ASSETS. The
// default is deliberately NOT offered as a pickable option — leaving the
// picker on "None" already resolves to it via resolveBlankLogoAsset, so an
// explicit "Fox Village mark" entry would just be a second way to say the
// same thing.
export const BLANK_LOGO_ASSET_OPTIONS = [
  { value: "wec-vertical-white", label: "WEC (vertical, white)" },
];

// Resolves a mode's `logoAsset` key to an image src. Never throws: an
// object/array/number key is simply coerced to a (non-matching) property
// lookup and falls through to the default, same as an unset or misspelled
// string would. This is the ONLY function in the app that should read
// BLANK_LOGO_ASSETS directly.
export function resolveBlankLogoAsset(logoAssetKey) {
  return BLANK_LOGO_ASSETS[logoAssetKey] || BLANK_LOGO_ASSETS[DEFAULT_BLANK_LOGO_ASSET_KEY];
}
